import React, { useState, useRef, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import { X, Download } from 'lucide-react';
import { auth } from '../firebase';
import mapboxgl from 'mapbox-gl';

const ProjectPDFSnapshot = ({ project, onClose, mapRef }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const canvasRef = useRef(null);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatEquipmentType = (type) => {
    if (!type) return 'Equipment';
    return type.charAt(0).toUpperCase() + type.slice(1).replace(/([A-Z])/g, ' $1');
  };

  const generatePDF = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      console.log('Starting PDF generation...');
      console.log('Map ref:', mapRef);
      console.log('Project:', project);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;

      // Try to capture the map canvas first
      let mapImage = null;
      let mapWidth = 0;
      let mapHeight = 0;

      if (mapRef && mapRef.current) {
        try {
          console.log('Attempting to capture map canvas...');
          const mapCanvas = mapRef.current.getCanvas();
          if (mapCanvas) {
            console.log('Map canvas found, converting to image...');
            mapImage = mapCanvas.toDataURL('image/png');
            mapWidth = mapCanvas.width;
            mapHeight = mapCanvas.height;
            console.log('Map canvas captured successfully:', { mapWidth, mapHeight });
          } else {
            console.log('No canvas found in map ref');
          }
        } catch (canvasError) {
          console.log('Canvas capture failed:', canvasError);
        }
      }

      // If no map canvas, try to get map center for static map
      if (!mapImage) {
        console.log('No map canvas, trying static map...');
        let mapCenter = null;
        
        // Try to get center from map ref
        if (mapRef && mapRef.current) {
          try {
            const center = mapRef.current.getCenter();
            if (center && center.lat && center.lng) {
              mapCenter = center;
              console.log('Got map center from map ref:', mapCenter);
            }
          } catch (e) {
            console.log('Could not get center from map ref:', e);
          }
        }

        // Fallback to equipment coordinates
        if (!mapCenter && project.equipment && project.equipment.length > 0) {
          const validEquipment = project.equipment.filter(eq => 
            eq.latitude && eq.longitude && 
            !isNaN(eq.latitude) && !isNaN(eq.longitude)
          );
          
          if (validEquipment.length > 0) {
            const avgLat = validEquipment.reduce((sum, eq) => sum + eq.latitude, 0) / validEquipment.length;
            const avgLng = validEquipment.reduce((sum, eq) => sum + eq.longitude, 0) / validEquipment.length;
            mapCenter = { lat: avgLat, lng: avgLng };
            console.log('Using average equipment coordinates as map center:', mapCenter);
          }
        }

        // Final fallback to default coordinates
        if (!mapCenter) {
          mapCenter = { lat: 40.7128, lng: -74.0060 }; // New York City
          console.log('Using default coordinates as map center:', mapCenter);
        }

        // Generate static map URL
        const zoom = 13;
        const width = 800;
        const height = 600;
        const staticMapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${mapCenter.lat},${mapCenter.lng}&zoom=${zoom}&size=${width}x${height}&maptype=roadmap&key=AIzaSyB41DRUbKWJHPxaFjMAwdrzWzbVKartNGg&scale=2`;
        
        console.log('Generated static map URL:', staticMapUrl);
        
        try {
          const response = await fetch(staticMapUrl);
          if (!response.ok) {
            throw new Error(`Static map request failed: ${response.status}`);
          }
          const blob = await response.blob();
          mapImage = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
          mapWidth = width * 2; // Account for scale=2
          mapHeight = height * 2;
          console.log('Static map loaded successfully');
        } catch (staticMapError) {
          console.log('Static map failed:', staticMapError);
          // Try alternative static map service
          try {
            const alternativeUrl = `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/${mapCenter.lng},${mapCenter.lat},${zoom}/${width}x${height}@2x?access_token=pk.eyJ1IjoiZ3JpZG1hcC1wcm8iLCJhIjoiY2x0Z2J0Z2J0Z2J0ZyJ9.example`;
            const altResponse = await fetch(alternativeUrl);
            if (altResponse.ok) {
              const altBlob = await altResponse.blob();
              mapImage = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(altBlob);
              });
              mapWidth = width * 2;
              mapHeight = height * 2;
              console.log('Alternative static map loaded successfully');
            }
          } catch (altError) {
            console.log('Alternative static map also failed:', altError);
          }
        }
      }

      // Header
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text(project.name, margin, 25);

      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(project.customer, margin, 35);

      // Project info section
      let yPosition = 50;
      
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Project Information', margin, yPosition);
      
      yPosition += 10;
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      
      pdf.text(`Project Type: ${project.type}`, margin, yPosition);
      yPosition += 6;
      pdf.text(`Customer: ${project.customer}`, margin, yPosition);
      yPosition += 6;
      
      if (project.customerAddress) {
        pdf.text(`Address: ${project.customerAddress}`, margin, yPosition);
        yPosition += 6;
      }
      
      if (project.customerPhone) {
        pdf.text(`Phone: ${project.customerPhone}`, margin, yPosition);
        yPosition += 6;
      }

      // Equipment summary
      yPosition += 5;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Equipment Summary', margin, yPosition);
      
      yPosition += 10;
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Equipment Items: ${project.equipment?.length || 0}`, margin, yPosition);
      yPosition += 6;
      pdf.text(`Conductors: ${project.conductors?.length || 0}`, margin, yPosition);

      // Map section
      if (mapImage) {
        yPosition += 10;
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Project Map', margin, yPosition);
        
        yPosition += 10;
        
        // Calculate map dimensions to fit on page
        const maxMapWidth = pageWidth - (2 * margin);
        const maxMapHeight = 120; // Fixed height for map section
        
        const mapAspectRatio = mapWidth / mapHeight;
        let mapDisplayWidth = maxMapWidth;
        let mapDisplayHeight = maxMapWidth / mapAspectRatio;
        
        if (mapDisplayHeight > maxMapHeight) {
          mapDisplayHeight = maxMapHeight;
          mapDisplayWidth = maxMapHeight * mapAspectRatio;
        }
        
        // Center the map horizontally
        const mapX = margin + (maxMapWidth - mapDisplayWidth) / 2;
        
        console.log('Adding map to PDF:', {
          x: mapX,
          y: yPosition,
          width: mapDisplayWidth,
          height: mapDisplayHeight
        });
        
        pdf.addImage(mapImage, 'PNG', mapX, yPosition, mapDisplayWidth, mapDisplayHeight);
        
        yPosition += mapDisplayHeight + 10;
        
        // Draw equipment symbols on the map
        if (project.equipment && project.equipment.length > 0) {
          console.log('Drawing equipment symbols on PDF map...');
          
          const validEquipment = project.equipment.filter(eq => 
            eq.latitude && eq.longitude && 
            !isNaN(eq.latitude) && !isNaN(eq.longitude)
          );
          
          if (validEquipment.length > 0) {
            // Calculate bounds for coordinate conversion
            const lats = validEquipment.map(eq => eq.latitude);
            const lngs = validEquipment.map(eq => eq.longitude);
            const minLat = Math.min(...lats);
            const maxLat = Math.max(...lats);
            const minLng = Math.min(...lngs);
            const maxLng = Math.max(...lngs);
            
            // Add some padding to bounds
            const latPadding = (maxLat - minLat) * 0.1;
            const lngPadding = (maxLng - minLng) * 0.1;
            
            validEquipment.forEach((eq, index) => {
              try {
                // Convert lat/lng to pixel coordinates on the map
                const pixelX = mapX + ((eq.longitude - (minLng - lngPadding)) / ((maxLng + lngPadding) - (minLng - lngPadding))) * mapDisplayWidth;
                const pixelY = yPosition + mapDisplayHeight - ((eq.latitude - (minLat - latPadding)) / ((maxLat + latPadding) - (minLat - latPadding))) * mapDisplayHeight;
                
                // Draw equipment symbol
                pdf.setFillColor(34, 197, 94); // Green
                pdf.circle(pixelX, pixelY, 2, 'F');
                
                // Add equipment label
                pdf.setFontSize(8);
                pdf.setFont('helvetica', 'normal');
                pdf.setTextColor(0, 0, 0);
                pdf.text(`${formatEquipmentType(eq.type)} ${index + 1}`, pixelX + 4, pixelY + 2);
                
                console.log(`Drew equipment ${index + 1} at pixel coordinates:`, { pixelX, pixelY });
              } catch (drawError) {
                console.log(`Error drawing equipment ${index + 1}:`, drawError);
              }
            });
          }
        }
        
        // Draw conductor lines on the map
        if (project.conductors && project.conductors.length > 0) {
          console.log('Drawing conductor lines on PDF map...');
          
          const validConductors = project.conductors.filter(cond => 
            cond.startLat && cond.startLng && cond.endLat && cond.endLng &&
            !isNaN(cond.startLat) && !isNaN(cond.startLng) && 
            !isNaN(cond.endLat) && !isNaN(cond.endLng)
          );
          
          if (validConductors.length > 0) {
            // Get all valid equipment for bounds calculation
            const validEquipment = project.equipment?.filter(eq => 
              eq.latitude && eq.longitude && 
              !isNaN(eq.latitude) && !isNaN(eq.longitude)
            ) || [];
            
            // Calculate bounds for coordinate conversion (include conductors)
            const allLats = [...validEquipment.map(eq => eq.latitude), ...validConductors.map(c => c.startLat), ...validConductors.map(c => c.endLat)];
            const allLngs = [...validEquipment.map(eq => eq.longitude), ...validConductors.map(c => c.startLng), ...validConductors.map(c => c.endLng)];
            const minLat = Math.min(...allLats);
            const maxLat = Math.max(...allLats);
            const minLng = Math.min(...allLngs);
            const maxLng = Math.max(...allLngs);
            
            // Add some padding to bounds
            const latPadding = (maxLat - minLat) * 0.1;
            const lngPadding = (maxLng - minLng) * 0.1;
            
            validConductors.forEach((conductor, index) => {
              try {
                // Convert start and end coordinates to pixel coordinates
                const startX = mapX + ((conductor.startLng - (minLng - lngPadding)) / ((maxLng + lngPadding) - (minLng - lngPadding))) * mapDisplayWidth;
                const startY = yPosition + mapDisplayHeight - ((conductor.startLat - (minLat - latPadding)) / ((maxLat + latPadding) - (minLat - latPadding))) * mapDisplayHeight;
                
                const endX = mapX + ((conductor.endLng - (minLng - lngPadding)) / ((maxLng + lngPadding) - (minLng - lngPadding))) * mapDisplayWidth;
                const endY = yPosition + mapDisplayHeight - ((conductor.endLat - (minLat - latPadding)) / ((maxLat + latPadding) - (minLat - latPadding))) * mapDisplayHeight;
                
                // Draw conductor line
                pdf.setDrawColor(37, 99, 235); // Blue
                pdf.setLineWidth(1);
                pdf.line(startX, startY, endX, endY);
                
                // Add conductor label at midpoint
                const midX = (startX + endX) / 2;
                const midY = (startY + endY) / 2;
                
                pdf.setFontSize(8);
                pdf.setFont('helvetica', 'normal');
                pdf.setTextColor(37, 99, 235);
                pdf.text(`${conductor.type || 'Conductor'} ${index + 1}`, midX + 2, midY - 2);
                
                console.log(`Drew conductor ${index + 1} from (${startX}, ${startY}) to (${endX}, ${endY})`);
              } catch (drawError) {
                console.log(`Error drawing conductor ${index + 1}:`, drawError);
              }
            });
          }
        }
      }

      // Project notes section
      yPosition += 20;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Project Notes', margin, yPosition);
      
      yPosition += 10;
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0, 0, 0);

      const allNotes = [];
      
      // Add project-level notes first
      if (project.notes) {
        allNotes.push({
          type: 'project',
          title: 'Project Notes',
          notes: project.notes,
          details: {}
        });
      }
      
      // Collect equipment notes
      project.equipment?.forEach((item, index) => {
        if (item.notes) {
          allNotes.push({
            type: 'equipment',
            title: `${formatEquipmentType(item.type)} ${index + 1}`,
            notes: item.notes,
            details: item.details
          });
        }
      });
      
      // Collect conductor notes
      project.conductors?.forEach((conductor, index) => {
        if (conductor.notes) {
          allNotes.push({
            type: 'conductor',
            title: `${conductor.type ? formatEquipmentType(conductor.type) : 'Conductor'} ${index + 1}`,
            notes: conductor.notes,
            details: {
              'Wire Size': conductor.wireSize,
              'Length': conductor.length ? `${conductor.length} ft` : 'N/A'
            }
          });
        }
      });

      if (allNotes.length === 0) {
        pdf.text('No notes have been added to this project yet.', margin, yPosition);
      } else {
        allNotes.forEach((note, index) => {
          if (yPosition > pageHeight - 30) {
            pdf.addPage();
            yPosition = 20;
          }
          
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'bold');
          pdf.text(note.title, margin, yPosition);
          
          yPosition += 6;
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          
          // Split notes into lines that fit the page width
          const maxWidth = pageWidth - (2 * margin);
          const words = note.notes.split(' ');
          let line = '';
          let lines = [];
          
          words.forEach(word => {
            const testLine = line + (line ? ' ' : '') + word;
            const testWidth = pdf.getTextWidth(testLine);
            if (testWidth > maxWidth) {
              lines.push(line);
              line = word;
            } else {
              line = testLine;
            }
          });
          if (line) lines.push(line);
          
          lines.forEach(line => {
            if (yPosition > pageHeight - 20) {
              pdf.addPage();
              yPosition = 20;
            }
            pdf.text(line, margin, yPosition);
            yPosition += 5;
          });
          
          // Add details if any
          if (Object.keys(note.details || {}).length > 0) {
            yPosition += 2;
            Object.entries(note.details).forEach(([key, value]) => {
              if (yPosition > pageHeight - 20) {
                pdf.addPage();
                yPosition = 20;
              }
              pdf.setFontSize(9);
              pdf.setTextColor(100, 100, 100);
              pdf.text(`${key}: ${value}`, margin + 5, yPosition);
              yPosition += 4;
            });
          }
          
          yPosition += 8;
        });
      }

      // Footer
      const footerY = pageHeight - 10;
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, margin, footerY);

      console.log('PDF generation completed successfully');
      pdf.save(`${project.name.replace(/[^a-zA-Z0-9]/g, '_')}_project_report.pdf`);

    } catch (err) {
      console.error('PDF generation failed:', err);
      setError('Failed to generate PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Export Project PDF</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <p className="text-gray-600">
            Generate a professional PDF report for <strong>{project.name}</strong> including:
          </p>
          
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Project information and customer details</li>
            <li>• Equipment summary and map visualization</li>
            <li>• All project notes and specifications</li>
            <li>• Professional formatting suitable for clients</li>
          </ul>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              onClick={generatePDF}
              disabled={isGenerating}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Generating...
                </>
              ) : (
                <>
                  <Download size={16} />
                  Generate PDF
                </>
              )}
            </button>
            
            <button
              onClick={onClose}
              disabled={isGenerating}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectPDFSnapshot; 